import React from 'react';
import texts from '../../texts';
import {
    Item,
    FormHelpHeader,
    SearchInput,
    VideoItem,
    OpenInExpert,
    EmptyStateSearch
} from './../FormHelpUI';
import styles from '../../styles/FormHelpDialog.module.scss';
import {Flex, GridContainer, GridItem, PageNav} from '@priority-software/priority-style-react';
import {openInXpertByDocType} from '../../actions/helpActions';
import {DocType, FormHelp, FormHelpPageState, Pages} from '../../types';
import {observer} from 'mobx-react';
import {isRTL} from '../../../../Localization';

export type ListPageProps = {
    title: string;
    docType: DocType;
    activePage: number;
    query?: string;
    formHelp?: FormHelp;
    isLoadingHelpData?: boolean;
    setHelpPageQuery: (query: string) => void;
    setHelpActiveListPage: (pageNumber: number) => void;
    removeHelpPage: () => void;
    addHelpPage: (page: FormHelpPageState) => void;
};

const ITEM_PER_PAGE = window.innerHeight > 780 ? 8 : 5;
export const ListPage = observer(
    ({
        title,
        docType,
        activePage = 1,
        query = '',
        formHelp,
        setHelpActiveListPage,
        setHelpPageQuery,
        removeHelpPage,
        addHelpPage
    }: ListPageProps) => {
        const getDataByType = () => {
            const helpData = formHelp;

            switch (docType) {
                case DocType.Document:
                    return {list: helpData?.documents, title: texts.documents};
                case DocType.FAQ:
                    return {list: helpData?.faqs, title: texts.faqs};
                case DocType.Video:
                    return {list: helpData?.videos, title: texts.videos};
                default:
                    return null;
            }
        };

        const {list, title: docTypeTitle} = getDataByType();

        const filteredList = list.filter(item =>
            item.text.toLowerCase().includes(query.toLowerCase())
        );
        const pagesCount = Math.ceil(filteredList.length / ITEM_PER_PAGE);

        const handleSearch = text => {
            setHelpActiveListPage(activePage);
            setHelpPageQuery(text);
        };

        const onItemClick = id => {
            addHelpPage({page: Pages.details, id});
        };
        const pageList = filteredList.slice(
            (activePage - 1) * ITEM_PER_PAGE,
            (activePage - 1) * ITEM_PER_PAGE + ITEM_PER_PAGE
        );

        const videoList = (
            <GridContainer columns={2}>
                {pageList.map((item, index) => (
                    <GridItem
                        key={`list_video_${index}`}
                        offset={index % 2 === 0 ? 1 : 2}
                        span={1}
                        componentClasses={styles.gridBlock}
                    >
                        <VideoItem query={query} item={item} />
                    </GridItem>
                ))}
            </GridContainer>
        );
        return (
            <Flex
                direction="column"
                justifyContent="flex-start"
                componentClasses={styles.container}
            >
                <FormHelpHeader
                    onBackClick={() => removeHelpPage()}
                    largeHeader
                    topTitle={title}
                    bottomTitle={docTypeTitle}
                />
                <div className={styles.content}>
                    {docType === DocType.FAQ && (
                        <SearchInput
                            value={query}
                            onClear={() => handleSearch('')}
                            onChange={text => handleSearch(text)}
                            placeholder={texts.inputListPlaceholder}
                        />
                    )}
                    <div className={styles.section}>
                        {pageList?.length > 0 ? (
                            docType === DocType.Video ? (
                                videoList
                            ) : (
                                pageList.map((item, index) => (
                                    <Item
                                        key={`list_item_${index}`}
                                        query={query}
                                        docType={docType}
                                        item={item}
                                        onClick={onItemClick}
                                    />
                                ))
                            )
                        ) : (
                            <EmptyStateSearch
                                onClick={() => openInXpertByDocType(docType)}
                                query={query}
                            />
                        )}
                    </div>
                </div>
                <Flex
                    componentClasses={styles.footer}
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                >
                    {pagesCount > 1 && (
                        <PageNav
                            activePage={activePage}
                            pagesCount={pagesCount}
                            rtl={isRTL()}
                            onItemClick={nextPage => setHelpActiveListPage(nextPage)}
                        />
                    )}
                    <OpenInExpert
                        onClick={() => {
                            openInXpertByDocType(docType, query);
                        }}
                    />
                </Flex>
            </Flex>
        );
    }
);
